import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/productSearch"
import { SALES_SEARCH_LIMIT, SUGGESTION_DEBOUNCE_MS } from "../constants"
import type { SalesProduct } from "../types"

import { MIN_SEARCH_LENGTH } from "@/constants/common"
import { ComputedProductStatus, GetSalesProductListDocument } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

interface UseGetSalesProductSuggestionsReturn {
  suggestions: SalesProduct[]
}

export const useGetSalesProductSuggestions = (
  searchInputValue: string,
  excludedProductIds: Set<number>
): UseGetSalesProductSuggestionsReturn => {
  const debouncedQuery = useDebounce(searchInputValue.trim(), SUGGESTION_DEBOUNCE_MS)
  const isQueryTooShort = debouncedQuery.length < MIN_SEARCH_LENGTH

  const [{ data }] = useQuery({
    query: GetSalesProductListDocument,
    variables: {
      input: {
        filter: {
          query: debouncedQuery,
          computedProductStatuses: [ComputedProductStatus.Active],
        },
        limit: SALES_SEARCH_LIMIT,
        offset: 0,
      },
    },
    pause: isQueryTooShort,
  })

  const suggestions = useMemo<SalesProduct[]>(() => {
    if (isQueryTooShort) return []
    if (data?.product?.list?.__typename !== "ProductListSuccess") return []
    return data.product.list.products.filter((product) => !excludedProductIds.has(product.productId))
  }, [data, excludedProductIds, isQueryTooShort])

  return { suggestions }
}
